import querystring from "query-string";
import { retrieveSessionStorage } from "./storageUtils";
import { Application } from "./constants";

export const readQueryString = (location, name) => {
  let params = querystring.parse(location.search);
  return params[name];
};

// What stands in front of the Portal's home address in the host name ("app", "www"), or "" when
// nothing does. The home address is a setting (REACT_APP_HOST): each environment has its own.
export const getSubdomain = (hostname, homeAddress) => {
  const lowerCaseHostname = hostname.toLowerCase();
  // A browser keeps the trailing dot of an address written in full (app.pellerex.com.)
  const host = lowerCaseHostname.endsWith(".")
    ? lowerCaseHostname.slice(0, -1)
    : lowerCaseHostname;
  const home = (homeAddress ?? "").toLowerCase();
  const homeSuffix = `.${home}`;

  if (home === "" || !host.endsWith(homeSuffix)) {
    return "";
  }

  return host.slice(0, -homeSuffix.length);
};

export const getAbsolutePath = ({ appName, path }) => {
  for (let i = 0; i < Apps.length; i++) {
    let app = Apps[i];
    if (app.appName === appName) {
      return getFQDN({
        subDomain: app.subDomain,
        path,
      });
    }
  }

  let mainApp = Apps.find((app) => app.main);
  return getFQDN({
    subDomain: mainApp.subDomain,
    path,
  });
};

export const Routes = {
  Website: {
    Home: "/",
    Terms: "/terms",
    Policies: "/policies",
    Pages: {
      Blogs: {
        Directory: "/blog/list",
      },
      Docs: {
        Base: "/docs",
        UrlTemplate: "/docs?id={docId}",
        PlatformWalkthrough: {
          Overview: "platform-walkthrough-overview",
          Day0Provisioning: "platform-walkthrough-day-0-provisioning",
          Day1ActiveDevelopment: "platform-walkthrough-day-1-active-development",
          Day2PerAppGovernance: "platform-walkthrough-day-2-per-app-governance",
          WorkflowEngine: "platform-walkthrough-workflow-engine",
          SinglePaneOfGlass: "platform-walkthrough-single-pane-of-glass",
          PlatformGovernance: "platform-walkthrough-platform-governance",
          DigitalTransformation: "platform-walkthrough-digital-transformation",
          CloudAbstraction: "platform-walkthrough-cloud-abstraction",
          EnterpriseDigitalPlatform: "platform-walkthrough-enterprise-digital-platform",
          InternalDigitalPlatforms: "platform-walkthrough-internal-digital-platforms",
          PaaS: "platform-walkthrough-paas",
          ApiMonetisation: "platform-walkthrough-api-monetisation",
          MicroservicePlatforms: "platform-walkthrough-microservice-platforms",
          RegulatedIndustries: "platform-walkthrough-regulated-industries",
          FinOps: "platform-walkthrough-finops",
          MultiCloudOperations: "platform-walkthrough-multi-cloud-operations",
          Startups: "platform-walkthrough-startups",
          ScaleUps: "platform-walkthrough-scale-ups",
          BrownfieldPlatforms: "platform-walkthrough-brownfield-platforms",
          PublicGovernmentAgency: "case-study-public-government-agency",
          HealthcareConglomerate: "case-study-healthcare-conglomerate",
          LargeRetailer: "case-study-large-retailer",
          SuperannuationCompany: "case-study-superannuation-company",
        },
        Platform: {
          Tenancy: "pellerex-tenancy",
          Organisation: "pellerex-organisations",
          AppSpaces: "pellerex-appspaces",
          Integrations: "pellerex-integrations",
          Accelerators: "pellerex-accelerators",
          Governance: "pellerex-governance",
          ServiceCatalog: "pellerex-service-catalog",
          AIAgentReadiness: "pellerex-ai-agent-readiness",
          DayOperations: "pellerex-day-operations",
          DeploymentTimelines: "pellerex-deployment-timelines",
        },
        ManagedApiService: {
          GettingStarted: "managed-api-service-getting-started",
          Scaffolding: "managed-api-services-scaffolding",
          DevOps: "managed-api-services-devops",
          SecretManagement: "managed-api-service-secret-management",
          Database: "managed-api-service-databases",
          Infrastructure: "managed-api-service-infrastructure",
          Consumption: "managed-api-service-consumption",
          Monitoring: "managed-api-service-monitoring",
          Compute: "managed-api-services-compute",
          Usage: "managed-api-service-usage",
          McpIntegration: "managed-api-service-mcp-integration",
        },
        ManagedIdentity: {
          AcquireJwt: "managed-identity-acquire-jwt",
        },
        Plans: {
          Subscribe: "plans-subscribe",
          PluginModel: "plans-plugin-model",
          Unsubscribe: "plans-unsubscribe",
        },
      },
      Latest: {
        Home: "/latest",
      },
      Conversion: {
        TalkToExpert: "/contact/expert",
        Prices: "/prices",
        Content: {
          Solutions: {
            IDP: "/solutions/idp",
          },
        },
      },
      Onboarding: {
        UserInvitationLandingPage: "/onboarding/invitation",
        UserInvitationAcceptancePage: "/onboarding/invitation/acceptance",
      },
      Others: {
        Terms: "/terms",
        Policies: "/policies",
        Status: "/status",
      },
    },
  },
  Application: {
    Identity: {
      Login: "/auth",
      Organisation: {
        Selection: "/organisations/selection",
      },
      Social: {
        AuthenticationUrlTemplate:
          "{baseUrl}?provider={provider}&returnUrl={returnUrl}&tenantId={tenantId}&organisationId={organisationId}&embeddedMode={embeddedMode}&audience={audience}&scopes={scopes}",
        ExternalLoginCallbackUrl:
          "/auth/external-login-callback-pellerex-frontend",
      },
      ChangePassword: "/change-password",
      RequestPasswordResetLink: "/request-password-reset-link",
      RequestEmailActivationLink: "/request-email-activation-link",
    },
    Contributors: {
      Directory: "/contributors",
      Profile: "/c",
      Pricing: "/company/pricing",
    },
    Dashboard: {
      Default: "/",
      Home: "/dashboard",
      DefaultLanding: "/dashboard/home",
      Apps: "/dashboard/apps",
      AppSpace: {
        Selection: "/app-space/selection",
      },
      Support: "/dashboard/support",
      Products: "/dashboard/products",
      Inbox: "/dashboard/inbox",
      Profile: "/dashboard/profile",
      // Deep links (deep-linking design): the canonical context route. Slugs
      // resolve to ids on entry; the optional screen and entity segments replay
      // as dashboard commands (common/deepLinks.js holds the mapping).
      DeepLink: {
        Prefix: "/dashboard/t/",
        Context:
          "/dashboard/t/:tenantSlug/o/:organisationSlug/s/:appSpaceSlug/:screen?/:entity?",
      },
    },
  },
};

// Per-handler tab segments for deep links (?tab=...). Kebab-case, hand-assigned,
// never derived from display copy at call sites; slugifyTabTitle in
// common/deepLinks.js folds tab titles to exactly these values.
export const DashboardTabSegments = {
  Identity: "identity",
  Gateway: "gateway",
  Domains: "domains",
  Mcp: "mcp",
  Codebase: "codebase",
  Ci: "ci",
  Cd: "cd",
  Secrets: "secrets",
  Database: "database",
  Infra: "infra",
  Uptime: "uptime",
  Logs: "logs",
  Traffic: "traffic",
  Vulnerabilities: "vulnerabilities",
  Dependencies: "dependencies",
  DevSecOps: "devsecops",
  Standards: "standards",
  TestCoverage: "test-coverage",
  Gpu: "gpu",
  Access: "access",
  Help: "help",
  Admin: "admin",
  FeatureFlags: "feature-flags",
};

//Redirect URL
export const getRedirectUrl = () => {
  let redirectUrl = retrieveSessionStorage(
    Application.StorageKeys.Navigation.RedirectUrl
  );

  let url = undefined;

  //If the user came from any of these screens before login, they should be redirected to home page.
  switch (true) {
    case redirectUrl?.pathname ===
      Routes.Application.Identity.Social.ExternalLoginCallbackUrl:
    case redirectUrl?.pathname === Routes.Application.Identity.ChangePassword:
    case redirectUrl?.pathname ===
      Routes.Application.Identity.RequestPasswordResetLink:
      url = "/";
      break;

    case redirectUrl?.pathname?.startsWith(
      Routes.Website.Pages.Onboarding.UserInvitationAcceptancePage
    ):
      url = redirectUrl?.pathname;
      break;

    // Deep links survive login: the stored pathname already carries its query
    // string (ProtectedRoute persists pathname + search).
    case redirectUrl?.pathname?.startsWith(
      Routes.Application.Dashboard.DeepLink.Prefix
    ):
      url = redirectUrl?.pathname;
      break;

    default:
      break;
  }

  if (url === undefined) {
    return "/";
  }

  return url;
};
